/*
 * Copyright (c) 2023 by N-Ach (Pvt) Ltd
 *
 * ╔═╗ ╔╗    ╔═══╗  ╔╗
 * ║║╚╗║║    ║╔═╗║  ║║
 * ║╔╗╚╝║    ║║ ║╠══╣╚═╗
 * ║║╚╗║║╔══╗║╚═╝║╔═╣╔╗║
 * ║║ ║║║╚══╝║╔═╗║╚═╣║║║
 * ╚╝ ╚═╝    ╚╝ ╚╩══╩╝╚╝
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information
 * of N-Ach (Pvt) Ltd. You shall not disclose such Confidential
 * Information and shall use it only in accordance with the terms
 * of the license agreement you entered into with N-Ach (Pvt) Ltd.
 *
 *
 * @Author Dilhari Jayathunga
 * @Date 25/5/2023
 */

import React, { useEffect, useRef } from "react";
import { Button, Container, Divider, IconButton, Typography } from "@mui/material";
import {
  AssessmentOutlined,
  Close,
  DashboardOutlined,
  LogoutOutlined,
  ListAltOutlined,
  PointOfSaleOutlined,
  SettingsOutlined,
  StorefrontOutlined,
  RestaurantOutlined,
  SoupKitchen,
  RoomServiceOutlined,
  LocalShippingOutlined,
  ReceiptLongOutlined as BillingIcon,
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { signOut } from "../../Store/Actions/signOut";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./SidePanel.module.css";

 const SidePanel = ({ open, onClose, collapsed = false, onExpand, onCollapse, themeMode = "light" }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef(null);

  const menuItems = [
    { label: "Dashboard", icon: <DashboardOutlined />, path: "/" },
    { label: "POS Supermarket", icon: <PointOfSaleOutlined />, path: "/supermarket" },
    { label: "POS Restaurant", icon: <RestaurantOutlined />, path: "/restaurant" },
    { label: "Kitchen", icon: <SoupKitchen />, path: "/kitchen" },
    { label: "Server", icon: <RoomServiceOutlined />, path: "/server" },
    { label: "Delivery", icon: <LocalShippingOutlined />, path: "/delivery" },
    { label: "Billing", icon: <BillingIcon />, path: "/billing" },
    { label: "Orders", icon: <ListAltOutlined  />, path: "/order" },
    { label: "Reports", icon: <AssessmentOutlined />, path: "/checkout" },
    // { label: "Settings", icon: <SettingsOutlined />, path: "/admin" },

  ];

  useEffect(() => {
    if (collapsed) {
      return undefined;
    }
    const handleOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        onCollapse && onCollapse();
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [collapsed, onCollapse]);

  const handleNavigation = (path) => {
    navigate(path);
    if (open) {
      onClose();
    }
  };

  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {open && <div className={styles.backdrop} onClick={onClose} aria-hidden="true" />}
      <div
        ref={containerRef}
        className={`${styles.container} ${open ? styles.open : ""} ${collapsed ? styles.collapsed : ""} ${themeMode === "dark" ? styles.dark : ""}`}
      >
      <Container className={styles.contentWrap}>
        <IconButton className={styles.closeButton} onClick={onClose}>
          <Close />
        </IconButton>
        <div className={styles.brandSection}>
          <div className={styles.brandBadge}>
            <StorefrontOutlined />
          </div>
          <div className={styles.brandText}>
            <Typography className={styles.brandTitle}>SmartPOS</Typography>
            <Typography className={styles.brandSub}>Retail Console</Typography>
          </div>
        </div>

        <div className={styles.list}>
          {menuItems.map((item) => (
            <button
              key={item.label}
              className={`${styles.menuItem} ${isActive(item.path) ? styles.selected : ""}`}
              onClick={() => handleNavigation(item.path)}
              title={item.label}
            >
              <span className={styles.menuIconBox}>
                <span className={styles.menuIcon}>{item.icon}</span>
              </span>
              <span className={styles.menuText}>{item.label}</span>
            </button>
          ))}
        </div>

        {!collapsed && (
          <>
            <Divider className={styles.divider} />

            <div className={styles.quickActions}>
              <Typography className={styles.quickTitle}>Quick Actions</Typography>
              <Button className={styles.quickButtonPrimary} onClick={() => handleNavigation("/")}>New Sale</Button>
              <Button className={styles.quickButton} onClick={() => handleNavigation("/billing")}>View Bills</Button>
              <Button className={styles.quickButton} onClick={() => handleNavigation("/checkout")}>Close Shift</Button>
            </div>
          </>
        )}

        <div className={styles.bottomListContainer}>
<Button
  className={styles.quickButton}
  startIcon={<LogoutOutlined />}
  title={collapsed ? "Logout" : ""}
  onClick={() => {
    dispatch(signOut());
    navigate("/pos", { replace: true });
  }}
>
  {!collapsed && (
    <span className={styles.logoutText}>Logout</span>
  )}
</Button>
        </div>
      </Container>
    </div>
    </>
  );
};

export default SidePanel;
